<template>
  <sticky-header-layout>
    <template #header>
      <list-page-header>
        <div slot="right">
          <el-button
            :disabled="isSaving"
            size="mini"
            type="primary"
            icon="el-icon-back"
            @click="handleCancel"
          >
            {{ $t('button.back') }}
          </el-button>
          
          <el-button
            :disabled="isLoading"
            :loading="isSaving"
            size="mini"
            type="danger"
            icon="el-icon-upload2"
            @click="handleSave"
          >
            {{ $t('button.save') }}
          </el-button>
        </div>
      </list-page-header>
    </template>

    <div v-loading="isLoading">
      <el-form
        v-loading="isLoading"
        ref="form"
        :disabled="isSaving"
        :model="form"
        :rules="formRules"
        class="round-border-form"
        label-position="top"
        label-width="200"
      >
        <h4 class="title">Address Detail</h4>

        <div class="form-group">
          <el-form-item
            label="Address Nickname"
            prop="nickname"
          >
            <el-input v-model="form.nickname" />
          </el-form-item>
        </div>

        <div class="form-group">
          <el-form-item
            label="Name"
            prop="name"
          >
            <el-input v-model="form.name" />
          </el-form-item>
        </div>

        <div class="form-group">
          <el-form-item
            label="Address"
            prop="address"
          >
            <el-input v-model="form.address" />
          </el-form-item>
        </div>
        
        <div class="form-group">
          <el-form-item
            label="Phone"
            prop="phone"
          >
            <el-input v-model="form.phone" />
          </el-form-item>
        </div>

        <div class="form-group">
          <el-form-item
            label="Remarks"
            prop="remarks"
          >
            <el-input v-model="form.remarks" />
          </el-form-item>
        </div>

        <div class="form-group">
          <el-form-item
            label="Default Address?"
            prop="is_default"
          >
            <el-switch 
              :active-value="1"
              :inactive-value="0"
              v-model="form.is_default" />
          </el-form-item>
        </div>
      </el-form>
    </div>
  </sticky-header-layout>
</template>

<script>
import { createComponent, computed, reactive, ref, watch, onBeforeMount } from '@vue/composition-api'
import ListPageHeader from '@/views/layout/components/ListPageHeader.vue'

import PrivateClientsService from '../../api/private-clients'
import { i18n } from '@/lang'

const PrivateClientAddressDetail = createComponent({
  name: 'PrivateClientAddressDetail',
  components: {
    ListPageHeader,
  },
  props: {
    id: {
      type: [String, Number],
      default: undefined,
    },
  },
  setup({ id }, { root, refs }) {
    const { $router, $route } = root

    const isLoading = ref(false)
    const isSaving = ref(false)

    const form = reactive({
      private_client_id: $route.params.privateClientId,
      nickname: null,
      name: null,
      address: null,
      phone: null,
      remarks: null,
      is_default: 0,
    })

    const formRules = reactive({
      nickname: [{ required: true, message: i18n.t('error.thisFieldIsRequired'), trigger: 'blur' }],
      name: [{ required: true, message: i18n.t('error.thisFieldIsRequired'), trigger: 'blur' }],
      address: [{ required: true, message: i18n.t('error.thisFieldIsRequired'), trigger: 'blur' }],
      phone: [{ required: true, message: i18n.t('error.thisFieldIsRequired'), trigger: 'blur' }],
    })

    const goToClientDetailPage = () => {
      $router.push({ path: `/private-client/client/edit/${$route.params.privateClientId}` })
    }

    const handleSaveSuccess = () => {
      isSaving.value = false

      goToClientDetailPage()
    }

    const handleSaveFail = (error) => {
      isSaving.value = false

      root.$alert(`Error: ${error}`, root.$t('error.somethingWentWrong'), {
        type: 'error',
        confirmButtonText: root.$t('button.ok'),
      })
    }

    onBeforeMount(async () => {
      if (id) {
        getDetail(id)
      }
    })

    const getDetail = async (id) => {
      isLoading.value = true

      try {
        const { data } = await PrivateClientsService.getAddress(id)
        
        Object.keys(data).forEach((key) => {
          if (key === 'type' && !data[key]) return
          form[key] = data[key]
        })
      } catch (error) {
        root.$alert(root.$t('error.recordNotFound'), root.$t('button.error'), {
          confirmButtonText: root.$t('button.ok'),
          type: 'error',
        }).then(() => {
          goToClientDetailPage()
        })
      } finally {
        isLoading.value = false
      }
    }

    const save = async (form) => {
      isSaving.value = true

      if (id) {
        try {
          await PrivateClientsService.editAddress(id, form)
          handleSaveSuccess()
        } catch (error) {
          handleSaveFail(error)
        }
      } else {
        try {
          await PrivateClientsService.createAddress(form)
          handleSaveSuccess()
        } catch (error) {
          handleSaveFail(error)
        }
      }
    }

    const handleCancel = () => goToClientDetailPage()

    const handleSave = () => {
      refs.form.validate().then(() => {
        save(form)
      }).catch((error) => {
        root.$alert(root.$t('error.pleaseFillInAndCheckErrors'), root.$t('common.oops'), {
          confirmButtonText: root.$t('button.ok'),
        })
      })
    }

    return {
      form,
      formRules,
      handleCancel,
      handleSave,
      isLoading,
      isSaving,
    }
  },
})

export default PrivateClientAddressDetail
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/form.scss";
</style>
